import { supabaseClient } from './supabase';
import { fetchGHGInventoryData } from '../components/charts/GHGInventorySupabase';
import { supabaseLocationService } from './supabaseLocationService';
import { PolicyScenario, fetchBaselineGHGData } from './policyImpactService';

export interface TimeSeriesEmissionData {
  year: number;
  lightDutyEmissions: number;
  heavyDutyEmissions: number;
  naturalGasEmissions: number;
  electricityEmissions: number;
  totalEmissions: number;
}

export interface PolicyTimeSeriesResult {
  baselineData: TimeSeriesEmissionData[];
  projectedData: TimeSeriesEmissionData[];
  location: string;
}

/**
 * Fetch EMFAC county filtered data for VMT projection using forecasting_ratio
 * Uses the same approach as GHG Inventory to get ghg_per_mile values
 */
async function fetchEMFACForecastingData(location: string): Promise<any[]> {
  // Resolve county for the city to query EMFAC at the county level
  const resolvedCounty = (supabaseLocationService.getCountyForCity(location) || location).trim();
  const baseCountyName = resolvedCounty.replace(/\s+County$/i, '');
  const emfacLocation = `${baseCountyName} County`;
  
  // Try common naming variants
  const locationVariations = [
    emfacLocation,
    baseCountyName,
    `${baseCountyName} County`,
    resolvedCounty
  ];
  
  for (const loc of locationVariations) {
    console.log(`Policy Tool: Trying EMFAC county filtered for location: ${loc}`);
    const { data, error } = await supabaseClient
      .from('emfac_county_filtered')
      .select('year, forecasting_ratio, ghg_per_mile, total_vmt')
      .eq('location_name', loc)
      .eq('class', 'LDV')
      .gte('year', 2020)
      .lte('year', 2045)
      .order('year', { ascending: true });
    
    if (error) {
      console.log('Policy Tool: EMFAC county filtered query error for', loc, ':', error);
      continue;
    }
    
    if (data && data.length > 0) {
      console.log('Policy Tool: EMFAC county filtered data found for', loc, ':', data);
      return data;
    }
  }
  
  console.log('Policy Tool: No EMFAC county filtered data found for any location variation:', locationVariations);
  
  // Fallback: Try California data (same as GHG Inventory)
  const californiaVariations = ['California', 'CA', 'California State', 'State of California'];
  
  for (const californiaName of californiaVariations) {
    console.log(`Policy Tool: Trying California EMFAC county filtered: ${californiaName}`);
    const { data: californiaData, error: californiaError } = await supabaseClient
      .from('emfac_county_filtered')
      .select('year, forecasting_ratio, ghg_per_mile, total_vmt')
      .eq('location_name', californiaName)
      .eq('class', 'LDV')
      .gte('year', 2020)
      .lte('year', 2045)
      .order('year', { ascending: true });
    
    if (californiaError) {
      console.log(`Policy Tool: California EMFAC county filtered query error for ${californiaName}:`, californiaError);
      continue;
    }
    
    if (californiaData && californiaData.length > 0) {
      console.log(`Policy Tool: Using California EMFAC county filtered data for ${californiaName}:`, californiaData);
      return californiaData;
    }
  }
  
  console.log('Policy Tool: No EMFAC data available');
  return [];
}

/**
 * Calculate historical emissions (2005-2023) using the same method as GHG Inventory
 */
async function calculateHistoricalEmissions(location: string): Promise<TimeSeriesEmissionData[]> {
  try {
    // Get the GHG inventory data which already uses the correct calculation method
    const inventoryData = await fetchGHGInventoryData(location);
    
    // Extract year-specific historical data from the inventory result
    const historicalData: TimeSeriesEmissionData[] = [];
    
    // Get the chart data which contains year-specific emissions
    const chartData = inventoryData.chartData;
    const years = chartData.labels.map((year: any) => parseInt(year));
    
    console.log('Policy Tool: Available years in GHG inventory:', years);
    
    // Pre-compute county EMFAC 2023 factor and 2023 VMT baseline for continuity
    let emfac2023Factor: number | null = null;
    let vmt2023Baseline: number | null = null;
    try {
      const emfacData = await fetchEMFACForecastingData(location);
      const emfac2023 = emfacData.find((d: any) => d.year === 2023 && d.ghg_per_mile > 0);
      if (emfac2023) emfac2023Factor = emfac2023.ghg_per_mile;

      await supabaseLocationService.initialize();
      const geofips = await supabaseLocationService.getGeofips(location);
      const { getVMTDataForGHG } = await import('./vmtDataService');
      const vmtData = await getVMTDataForGHG(location, geofips || undefined);
      const vmt2023 = vmtData.find((d: any) => d.year === 2023);
      if (vmt2023) vmt2023Baseline = vmt2023.vmt;
    } catch (e) {
      console.warn('Policy Tool: Could not pre-compute EMFAC/VMT for 2023 continuity:', e);
    }

    // Extract datasets for all categories
    const lightDutyDataset = chartData.datasets.find((d: any) => d.label === 'Light Duty Vehicles');
    const heavyDutyDataset = chartData.datasets.find((d: any) => d.label === 'Heavy Duty Vehicles');
    const naturalGasResDataset = chartData.datasets.find((d: any) => d.label === 'Natural Gas Residential');
    const naturalGasComDataset = chartData.datasets.find((d: any) => d.label === 'Natural Gas Commercial');
    const electricityResDataset = chartData.datasets.find((d: any) => d.label === 'Electricity Residential');
    const electricityComDataset = chartData.datasets.find((d: any) => d.label === 'Electricity Commercial');

    // Process all years from 2005 to 2023
    for (let year = 2005; year <= 2023; year++) {
      const yearIndex = years.indexOf(year);
      
      if (yearIndex !== -1) {
        // Extract year-specific data from chart datasets
        let lightDutyEmissions = lightDutyDataset?.data[yearIndex] || 0;
        // Ensure continuity at 2023 by recomputing LDV with county EMFAC factor if available
        if (year === 2023 && emfac2023Factor && vmt2023Baseline) {
          lightDutyEmissions = vmt2023Baseline * emfac2023Factor;
        }
        const heavyDutyEmissions = heavyDutyDataset?.data[yearIndex] || 0;
        
        // Combine residential and commercial natural gas emissions
        const naturalGasRes = naturalGasResDataset?.data[yearIndex] || 0;
        const naturalGasCom = naturalGasComDataset?.data[yearIndex] || 0;
        const naturalGasEmissions = naturalGasRes + naturalGasCom;
        
        // Combine residential and commercial electricity emissions
        const electricityRes = electricityResDataset?.data[yearIndex] || 0;
        const electricityCom = electricityComDataset?.data[yearIndex] || 0;
        const electricityEmissions = electricityRes + electricityCom;
        
        historicalData.push({
          year,
          lightDutyEmissions: Math.round(lightDutyEmissions),
          heavyDutyEmissions: Math.round(heavyDutyEmissions),
          naturalGasEmissions: Math.round(naturalGasEmissions),
          electricityEmissions: Math.round(electricityEmissions),
          totalEmissions: Math.round(lightDutyEmissions + heavyDutyEmissions + naturalGasEmissions + electricityEmissions)
        });
        
        if (year >= 2020 || year % 5 === 0) {
          console.log(`Policy Tool: Historical ${year}: LD=${lightDutyEmissions}, HD=${heavyDutyEmissions}, NG=${naturalGasEmissions}, EL=${electricityEmissions}`);
        }
      } else {
        // Fallback: use latest year's data if specific year not available
        const latestYearIndex = years.length - 1;
        const latestLightDuty = lightDutyDataset?.data[latestYearIndex] || 0;
        const latestHeavyDuty = heavyDutyDataset?.data[latestYearIndex] || 0;
        const latestNaturalGasRes = naturalGasResDataset?.data[latestYearIndex] || 0;
        const latestNaturalGasCom = naturalGasComDataset?.data[latestYearIndex] || 0;
        const latestElectricityRes = electricityResDataset?.data[latestYearIndex] || 0;
        const latestElectricityCom = electricityComDataset?.data[latestYearIndex] || 0;
        
        historicalData.push({
          year,
          lightDutyEmissions: Math.round(latestLightDuty),
          heavyDutyEmissions: Math.round(latestHeavyDuty),
          naturalGasEmissions: Math.round(latestNaturalGasRes + latestNaturalGasCom),
          electricityEmissions: Math.round(latestElectricityRes + latestElectricityCom),
          totalEmissions: Math.round(latestLightDuty + latestHeavyDuty + latestNaturalGasRes + latestNaturalGasCom + latestElectricityRes + latestElectricityCom)
        });
      }
    }
    
    console.log('Policy Tool: Historical emissions calculated:', historicalData);
    return historicalData;
  } catch (error) {
    console.error('Error calculating historical emissions:', error);
    return [];
  }
}

/**
 * Project future emissions (2023-2045) using EMFAC forecasting data
 */
async function calculateProjectedEmissions(
  location: string, 
  historicalData: TimeSeriesEmissionData[],
  vmtReductionPercent: number = 0,
  evAdoptionPercent: number = 0,
  currentEVPercent: number = 0,
  scenario?: PolicyScenario
): Promise<TimeSeriesEmissionData[]> {
  try {
    // Get EMFAC forecasting data
    const emfacData = await fetchEMFACForecastingData(location);
    
    // Get VMT data from Transportation page (includes user customizations) - same as GHG Inventory
    let geofips: number | null = null;
    try {
      await supabaseLocationService.initialize();
      geofips = await supabaseLocationService.getGeofips(location);
    } catch (error) {
      console.warn('Could not get geofips for VMT data:', error);
    }
    
    // Import and use the same VMT data service as GHG Inventory
    const { getVMTDataForGHG } = await import('./vmtDataService');
    const vmtData = await getVMTDataForGHG(location, geofips || undefined);
    console.log('VMT data for projection:', vmtData);
    
    // Get the latest VMT value (2023) as baseline
    const latestVmtData = vmtData.find(d => d.year === 2023);
    const baselineVmt = latestVmtData?.vmt || 0;
    
    console.log('Baseline VMT (2023):', baselineVmt);
    
    const projectedData: TimeSeriesEmissionData[] = [];
    
    // Calculate fallback average emission factor from available EMFAC data
    const validEmissionFactors = emfacData.filter(d => d.ghg_per_mile > 0);
    const avgEmissionFactor = validEmissionFactors.length > 0 
      ? validEmissionFactors.reduce((sum, d) => sum + d.ghg_per_mile, 0) / validEmissionFactors.length
      : 0.0008; // Fallback emission factor (tons CO2 per mile)
    
    console.log('Policy Tool: Fallback average emission factor:', avgEmissionFactor, 'tons CO2 per mile');
    console.log('Policy Tool: Available emission factors:', validEmissionFactors.map(d => `${d.year}: ${d.ghg_per_mile}`));
    
    // If we have an EMFAC emission factor for 2023, recompute LDV 2023 with it
    const emfac2023 = emfacData.find(d => d.year === 2023 && d.ghg_per_mile > 0);
    
    // Pre-fetch baseline data and calculate grid intensity if EV adoption is used
    let baseline: any = null;
    let gridIntensity: number | null = null;
    const CONSTANTS = {
      BASELINE_GRID_INTENSITY: 0.2,
      ANNUAL_SOLAR_HOURS: 2000,
      SOLAR_DISPLACEMENT_FACTOR: 0.85,
      EV_KWH_PER_MILE: 0.3,
      ICE_IMPROVEMENT_FACTOR: 0.95,
    };
    
    if (scenario && evAdoptionPercent > currentEVPercent) {
      try {
        baseline = await fetchBaselineGHGData(location);
        const solarMult = scenario.solarCapacityMultiplier || 1;
        const solarCapacityKw = baseline.solarCapacityKw * solarMult;
        const totalElectricityConsumption = baseline.population * 7000;
        const solarGeneration = solarCapacityKw * CONSTANTS.ANNUAL_SOLAR_HOURS;
        const solarPenetration = totalElectricityConsumption > 0 ? solarGeneration / totalElectricityConsumption : 0;
        gridIntensity = CONSTANTS.BASELINE_GRID_INTENSITY * (1 - Math.min(solarPenetration * CONSTANTS.SOLAR_DISPLACEMENT_FACTOR, 0.9));
      } catch (e) {
        console.warn('Could not fetch baseline data for EV calculation:', e);
      }
    }

    for (let year = 2023; year <= 2045; year++) {
      // For 2023, use actual historical data (don't project)
      if (year === 2023) {
        const historical2023 = historicalData.find(d => d.year === 2023);
        // Smooth continuity: prefer county EMFAC 2023 factor if available
        const ld2023FromEmfac = emfac2023 ? baselineVmt * emfac2023.ghg_per_mile : undefined;
        const lightDuty2023 = ld2023FromEmfac !== undefined ? Math.round(ld2023FromEmfac) : (historical2023?.lightDutyEmissions || 0);
        projectedData.push({
          year: 2023,
          lightDutyEmissions: lightDuty2023,
          heavyDutyEmissions: historical2023?.heavyDutyEmissions || 0,
          naturalGasEmissions: historical2023?.naturalGasEmissions || 0,
          electricityEmissions: historical2023?.electricityEmissions || 0,
          totalEmissions: lightDuty2023 +
            (historical2023?.heavyDutyEmissions || 0) +
            (historical2023?.naturalGasEmissions || 0) +
            (historical2023?.electricityEmissions || 0)
        });
        continue;
      }
      
      // Find EMFAC data for this year
      const emfacYearData = emfacData.find(d => d.year === year);
      
      if (emfacYearData && emfacYearData.forecasting_ratio) {
        // Project VMT using forecasting ratio
        const projectedVmt = baselineVmt * emfacYearData.forecasting_ratio;
        
        // Use year-specific emission factor from EMFAC data, or fallback to average
        const yearEmissionFactor = emfacYearData.ghg_per_mile || avgEmissionFactor;
        
        // Calculate gradual VMT reduction percentage for this year
        // Gradual ramp: increases by targetVMTReduction% / (2045 - 2023) each year from 2024 to 2045
        // Formula: annualVMTReductionIncrease = targetVMTReduction% / (2045 - 2023)
        // For year Y: vmtReductionPercentThisYear = 0 + annualIncrease * (Y - 2023)
        const yearsTo2045 = 2045 - 2023; // Total years from 2023 to 2045 (22 years)
        const yearsElapsed = year - 2023; // Years elapsed since 2023
        const annualVMTReductionIncrease = vmtReductionPercent / yearsTo2045; // Annual increase in VMT reduction percentage
        
        // Calculate VMT reduction percentage for this year
        const vmtReductionPercentThisYear = yearsElapsed <= 0 
          ? 0 // 2023: no VMT reduction yet
          : Math.min(vmtReductionPercent, annualVMTReductionIncrease * yearsElapsed); // 2024-2045: gradual increase
        
        // Apply gradual VMT reduction from policy tool
        const reducedVmt = projectedVmt * (1 - vmtReductionPercentThisYear / 100);
        
        // Calculate EV adoption percentage for this year
        // Gradual ramp: increases by (target - current) / (2045 - 2023) each year from 2024 to 2045
        // Formula: annualIncrease = (targetEV% - currentEV%) / (2045 - 2023)
        // For year Y: evPercentThisYear = currentEVPercent + annualIncrease * (Y - 2023)
        const annualEVIncrease = (evAdoptionPercent - currentEVPercent) / yearsTo2045; // Annual increase percentage
        
        // Calculate EV percentage for this year
        const evPercentThisYear = yearsElapsed <= 0 
          ? currentEVPercent // 2023: use current percentage (no change yet)
          : Math.min(evAdoptionPercent, currentEVPercent + (annualEVIncrease * yearsElapsed)); // 2024-2045: gradual increase
        
        // Calculate fleet-weighted emission factor accounting for EV adoption
        // EV adoption gradually reduces emissions each year as fleet electrifies
        let fleetEmissionFactor = yearEmissionFactor;
        if (scenario && evPercentThisYear >= currentEVPercent && baseline && gridIntensity !== null) {
          // Calculate ICE and EV emission factors
          const iceGhgPerMile = yearEmissionFactor * CONSTANTS.ICE_IMPROVEMENT_FACTOR;
          const evGhgPerMile = gridIntensity * CONSTANTS.EV_KWH_PER_MILE; // kg CO2/kWh * kWh/mile = kg CO2/mile, convert to tons
          const evGhgPerMileTons = evGhgPerMile / 1000; // Convert kg to tons
          
          const icePercent = 100 - evPercentThisYear;
          
          // Fleet-weighted emission factor: weighted average of ICE and EV emission factors
          fleetEmissionFactor = (icePercent / 100 * iceGhgPerMile) + (evPercentThisYear / 100 * evGhgPerMileTons);
        }
        
        // Calculate emissions: VMT * fleet-weighted emission factor
        const lightDutyEmissions = reducedVmt * fleetEmissionFactor;
        
        // Log EV adoption and VMT reduction progress for debugging (log first few years and every 5 years)
        if (year <= 2027 || year % 5 === 0 || year === 2045) {
          console.log(`Year ${year}: VMT Reduction=${vmtReductionPercentThisYear.toFixed(2)}% (target: ${vmtReductionPercent}%, annual increase: ${annualVMTReductionIncrease.toFixed(3)}%/year), EV%=${evPercentThisYear.toFixed(2)}% (target: ${evAdoptionPercent}%, annual increase: ${annualEVIncrease.toFixed(3)}%/year), Fleet EF=${fleetEmissionFactor.toFixed(6)} tons/mile, Emissions=${lightDutyEmissions.toFixed(0)} tons CO2e`);
        }
        
        // For other sources, use historical values with some growth assumptions
        const historical2023 = historicalData.find(d => d.year === 2023);
        const growthFactor = 1 + (year - 2023) * 0.02; // 2% annual growth assumption
        
        projectedData.push({
          year,
          lightDutyEmissions: Math.round(lightDutyEmissions),
          heavyDutyEmissions: Math.round((historical2023?.heavyDutyEmissions || 0) * growthFactor),
          naturalGasEmissions: Math.round((historical2023?.naturalGasEmissions || 0) * growthFactor),
          electricityEmissions: Math.round((historical2023?.electricityEmissions || 0) * growthFactor), // Project electricity similar to natural gas
          totalEmissions: Math.round(lightDutyEmissions) + 
                         Math.round((historical2023?.heavyDutyEmissions || 0) * growthFactor) +
                         Math.round((historical2023?.naturalGasEmissions || 0) * growthFactor) +
                         Math.round((historical2023?.electricityEmissions || 0) * growthFactor)
        });
      } else {
        // Fallback: use historical data with growth
        const historical2023 = historicalData.find(d => d.year === 2023);
        const growthFactor = 1 + (year - 2023) * 0.02;
        
        projectedData.push({
          year,
          lightDutyEmissions: Math.round((historical2023?.lightDutyEmissions || 0) * growthFactor),
          heavyDutyEmissions: Math.round((historical2023?.heavyDutyEmissions || 0) * growthFactor),
          naturalGasEmissions: Math.round((historical2023?.naturalGasEmissions || 0) * growthFactor),
          electricityEmissions: Math.round((historical2023?.electricityEmissions || 0) * growthFactor), // Project electricity similar to natural gas
          totalEmissions: Math.round((historical2023?.lightDutyEmissions || 0) * growthFactor) +
                         Math.round((historical2023?.heavyDutyEmissions || 0) * growthFactor) +
                         Math.round((historical2023?.naturalGasEmissions || 0) * growthFactor) +
                         Math.round((historical2023?.electricityEmissions || 0) * growthFactor)
        });
      }
    }
    
    console.log('Projected emissions calculated:', projectedData);
    return projectedData;
  } catch (error) {
    console.error('Error calculating projected emissions:', error);
    return [];
  }
}

/**
 * Main function to generate time series data for policy impact tool
 */
export async function generatePolicyTimeSeriesData(
  location: string,
  scenario: PolicyScenario
): Promise<PolicyTimeSeriesResult> {
  try {
    console.log('Generating policy time series data for', location, 'with scenario:', scenario);
    
    // Calculate historical emissions (2020-2023)
    const historicalData = await calculateHistoricalEmissions(location);
    
    // Fetch baseline data to get current EV percentage
    const baseline = await fetchBaselineGHGData(location);
    const currentEVPercent = baseline.currentEVPercent || 0;
    const targetEVPercent = scenario.evAdoptionPercent || 0;
    
    // Calculate projected emissions (2023-2045) for baseline (no policies, no EV adoption)
    const baselineProjected = await calculateProjectedEmissions(location, historicalData, 0, currentEVPercent, currentEVPercent);
    
    // Calculate projected emissions with policies applied (VMT reduction and EV adoption)
    const withPoliciesProjected = await calculateProjectedEmissions(
      location, 
      historicalData, 
      scenario.vmtReductionPercent || 0,
      targetEVPercent,
      currentEVPercent,
      scenario
    );
    
    // Combine historical and baseline projections
    const baselineData = [...historicalData, ...baselineProjected];

    // Apply simple policy scalers to non-LDV sources so lines diverge with settings
    const hdvScale = Math.max(0, 1 - (scenario.hdvElectrificationPercent || 0) / 100);
    const ngScale = Math.max(0, 1 - (scenario.heatPumpAdoptionPercent || 0) / 100);
    // Simple proxy for grid decarbonization from solar capacity multiplier
    const solarMult = scenario.solarCapacityMultiplier || 1;
    const elecReduction = Math.min(0.6, Math.max(0, (solarMult - 1) * 0.2)); // up to 60% reduction
    const elecScale = 1 - elecReduction;

    const projectedWithPolicies = [...historicalData, ...withPoliciesProjected].map((row, idx) => {
      // Use baseline for other sources and scale
      const baseRow = ([...historicalData, ...baselineProjected][idx]) || row;
      return {
        year: row.year,
        lightDutyEmissions: row.lightDutyEmissions, // already includes VMT policy
        heavyDutyEmissions: Math.round((baseRow.heavyDutyEmissions || 0) * hdvScale),
        naturalGasEmissions: Math.round((baseRow.naturalGasEmissions || 0) * ngScale),
        electricityEmissions: Math.round((baseRow.electricityEmissions || 0) * elecScale),
        totalEmissions: Math.round(row.lightDutyEmissions +
          (baseRow.heavyDutyEmissions || 0) * hdvScale +
          (baseRow.naturalGasEmissions || 0) * ngScale +
          (baseRow.electricityEmissions || 0) * elecScale)
      } as TimeSeriesEmissionData;
    });
    
    return {
      baselineData,
      projectedData: projectedWithPolicies,
      location
    };
  } catch (error) {
    console.error('Error generating policy time series data:', error);
    throw error;
  }
}
